import React, {
  useEffect,
  useState,
} from "react";
import {
  Button,
  Input,
  Table,
  Tooltip,
  Form,
  notification,
  PageHeader,
  Row,
  Col,
  Select,
  DatePicker,
} from "antd";

import IconMobile from "../../static/icon/IconMobile";
import ActionModal from "./ActionModal";
import BulkActiveModal from "./BulkActiveModal";

import {
  DeleteTwoTone,
  EditTwoTone,
  PlusOutlined,
  SearchOutlined,
  SyncOutlined,
  UploadOutlined,
} from "@ant-design/icons";
import IconNotFound from "../../static/icon/IconNotFound";

import DeleteAction from "../../Tools/DeleteAction";
import { useAuth } from "../../context/auth";
import { formatDateTimeBangkok, formatUserType, listUserType } from "../../utils/formatter";

// The picker gives a local calendar day; the account age is judged in GMT+7,
// so anchor the boundaries to that offset instead of the operator's timezone.
function startOfDayBangkok(date) {
  return `${date.format('YYYY-MM-DD')}T00:00:00+07:00`;
}
function endOfDayBangkok(date) {
  return `${date.format('YYYY-MM-DD')}T23:59:59.999+07:00`;
}

const MemberMobileUser = () => {
  const { accessToken } = useAuth();

  const [form] = Form.useForm();

  const [loading, setLoading] = useState(false);

  const [update, setUpdate] = useState(0);
  const [total, setTotal] = useState(0);
  const [users, setUsers] = useState([]);
  const [search, setSearch] = useState({
    pageValue: {},
    searchValue: {},
  });

  useEffect(() => {
    form.setFieldsValue(search.searchValue);
  }, [form, search]);

  const [modalType, setModalType] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);

  useEffect(() => {
    async function getUsers({ pageValue, searchValue }) {
      setLoading(true);

      const url = new URL('/api/user', window.location);
      url.searchParams.append('admin', 'false');
      if (searchValue.name) url.searchParams.append('name', searchValue.name);
      if (searchValue.email) url.searchParams.append('email', searchValue.email);
      if (typeof searchValue.emailVerified === 'boolean') url.searchParams.append('emailVerified', searchValue.emailVerified);
      if (searchValue.tel) url.searchParams.append('tel', searchValue.tel);
      if (typeof searchValue.telVerified === 'boolean') url.searchParams.append('telVerified', searchValue.telVerified);
      if (typeof searchValue.active === 'boolean') url.searchParams.append('active', searchValue.active);
      if (searchValue.type) url.searchParams.append('type', searchValue.type);
      if (searchValue.createdAfter) url.searchParams.append('createdAfter', startOfDayBangkok(searchValue.createdAfter));
      if (searchValue.createdBefore) url.searchParams.append('createdBefore', endOfDayBangkok(searchValue.createdBefore));

      if (pageValue.page) url.searchParams.append('page', pageValue.page);
      if (pageValue.size) url.searchParams.append('size', pageValue.size);
      if (pageValue.sort) url.searchParams.append('sort', pageValue.sort);
      if (pageValue.order) url.searchParams.append('order', pageValue.order);

      const res = await fetch(url, {
        headers: { 'X-ACCESS-TOKEN': accessToken },
      });
      if (res.ok) {
        const { total, users } = await res.json();
        setTotal(total);
        setUsers(users);
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }

      setLoading(false);
    };

    getUsers(search);
  }, [search, accessToken, update]);

  const columns = [
    {
      title: "Type",
      dataIndex: "type",
      key: "type",
      sorter: () => null,
      render: (type) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {formatUserType(type)}
        </span>
      ),
    },
    {
      title: "Name-Surname",
      dataIndex: "firstName",
      key: "firstName",
      sorter: () => null,
      render: (_, user) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {user.firstName} {user.lastName}
        </span>
      ),
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
      align: "center",
      sorter: () => null,
    },
    {
      title: "Verify Email",
      dataIndex: "emailVerified",
      key: "emailVerified",
      sorter: () => null,
      render: (emailVerified) => (
        <span style={{ color: emailVerified ? "#55B491" : "#EB5757" }}  >
          {emailVerified ? 'Verified' : 'Not Verified'}
        </span>
      ),
    },
    {
      title: "Mobile Number",
      dataIndex: "tel",
      key: "tel",
      align: "center",
      sorter: () => null,
    },
    {
      title: "Verify Mobile Number",
      dataIndex: "telVerified",
      key: "telVerified",
      sorter: () => null,
      render: (telVerified) => (
        <span style={{ color: telVerified ? "#55B491" : "#EB5757" }}  >
          {telVerified ? 'Verified' : 'Not Verified'}
        </span>
      ),
    },
    {
      title: "Created Date",
      dataIndex: "createdDate",
      key: "createdDate",
      align: "center",
      width: 150,
      sorter: () => null,
      render: (createdDate) => (
        <span style={{ whiteSpace: "nowrap" }}>
          {createdDate ? formatDateTimeBangkok(new Date(createdDate)) : '-'}
        </span>
      ),
    },
    {
      title: "Status",
      dataIndex: "active",
      key: "active",
      width: 120,
      sorter: () => null,
      render: (active) => (
        <span style={{ color: active ? "#55B491" : "#EB5757" }}  >
          {active ? 'Available' : 'Locked'}
        </span>
      ),
    },
    {
      title: "Action",
      key: "action",
      align: "center",
      width: "12%",
      render: (_, user) => (
        <span style={{ whiteSpace: "nowrap" }}>
          <Tooltip placement="bottom" title="Edit" className="mx-1">
            <EditTwoTone
              style={{
                backgroundColor: "#F3F6F9",
                padding: 6,
                borderRadius: 6,
              }}
              onClick={() => {
                setModalType("edit");
                setCurrentUser(user);
              }}
            />
          </Tooltip>
          {!user.active && (
            <Tooltip placement="bottom" title="Delete" className="mx-1">
              <DeleteTwoTone
                style={{
                  backgroundColor: "#F3F6F9",
                  padding: 6,
                  borderRadius: 6,
                }}
                onClick={() => {
                  setModalType("delete");
                  setCurrentUser(user);
                }}
                twoToneColor="red"
              />
            </Tooltip>
          )}
        </span>
      ),
    },
  ];

  const [bulkVisible, setBulkVisible] = useState(false);

  const [downloading, setDownloading] = useState(false);
  async function downloadXlsx({ searchValue }) {
    setDownloading(true);

    const url = new URL('/api/user', window.location);
    url.searchParams.append('admin', 'false');
    url.searchParams.append('mode', 'xlsx');

    if (searchValue.name) url.searchParams.append('name', searchValue.name);
    if (searchValue.email) url.searchParams.append('email', searchValue.email);
    if (typeof searchValue.emailVerified === 'boolean') url.searchParams.append('emailVerified', searchValue.emailVerified);
    if (searchValue.tel) url.searchParams.append('tel', searchValue.tel);
    if (typeof searchValue.telVerified === 'boolean') url.searchParams.append('telVerified', searchValue.telVerified);
    if (typeof searchValue.active === 'boolean') url.searchParams.append('active', searchValue.active);
    if (searchValue.type) url.searchParams.append('type', searchValue.type);
    if (searchValue.createdAfter) url.searchParams.append('createdAfter', startOfDayBangkok(searchValue.createdAfter));
    if (searchValue.createdBefore) url.searchParams.append('createdBefore', endOfDayBangkok(searchValue.createdBefore));

    const res = await fetch(url, {
      headers: { 'X-ACCESS-TOKEN': accessToken },
    });
    if (res.ok) {
      const data = await res.blob();
      const a = document.createElement('a');
      a.href = URL.createObjectURL(data);
      a.download = `User-Mobile_${new Date().toISOString()}.xlsx`;
      a.click();
    } else {
      if (res.status === 404) {
        notification["error"]({
          message: "No matched user mobile found",
        });
      } else {
        notification["error"]({
          message: "Something went wrong !",
          description: `Please re-login or contact web admin. (Code: ${await res.json().then(data => data.message)})`,
        });
      }
    }

    setDownloading(false);
  }

  return (
    <>
      <div className="flex justify-between items-center mt-2">
        <PageHeader
          style={{ padding: 0, width: "100%" }}
          title={
            <span className="flex items-center mt-2">
              <IconMobile /> &nbsp;&nbsp;Member Mobile User
            </span>
          }
        />

        <Button
          className="mt-2 mr-3 flex items-center"
          icon={<UploadOutlined />}
          type="primary"
          ghost
          shape="round"
          onClick={() => setBulkVisible(true)}
        >
          Bulk Active/Inactive
        </Button>

        <Button
          className="mt-2 mr-3 flex items-center px-12 border-0"
          loading={downloading}
          type="primary"
          shape="round"
          style={{ background: "#1BC5BD" }}
          onClick={() => downloadXlsx(search)}
        >
          Export
        </Button>

        <Button
          className="mt-2 flex items-center"
          icon={<PlusOutlined />}
          type="primary"
          shape="round"
          onClick={() => {
            setModalType("new");
            setCurrentUser({});
          }}
        >
          New Mobile User
        </Button>
      </div>

      <div className="content">
        <div className="">
          <Form
            form={form}
            layout="vertical"
            onFinish={(values) => setSearch(search => ({
              pageValue: { ...search.pageValue, page: 1 },
              searchValue: values,
            }))}
            onReset={() => form.submit()}
          >
            <Row align="middle" gutter={16}>
              <Col className="gutter-row" span={5}>
                <Form.Item
                  label="Name-Surname"
                  name="name"
                  colon={false}
                  initialValue={""}
                >
                  <Input
                    placeholder="Name-Surname"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={5}>
                <Form.Item
                  label="Email"
                  name="email"
                  colon={false}
                  initialValue={""}
                >
                  <Input
                    placeholder="Email"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Verify Email"
                  name="emailVerified"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Status' },
                      { value: true, label: 'Verified' },
                      { value: false, label: 'Not Verified' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={5}>
                <Form.Item
                  label="Mobile Number"
                  name="tel"
                  colon={false}
                  initialValue={""}
                >
                  <Input
                    placeholder="Mobile Number"
                    allowClear
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={5}>
                <Form.Item
                  label="Verify Mobile Number"
                  name="telVerified"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Status' },
                      { value: true, label: 'Verified' },
                      { value: false, label: 'Not Verified' },
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={4}>
                <Form.Item
                  label="Type"
                  name="type"
                  colon={false}
                  initialValue={null}
                >
                  <Select
                    showSearch
                    allowClear
                    options={[
                      { value: null, label: 'Any Role' },
                      ...listUserType.map(userType => ({
                        value: userType.name,
                        label: userType.value,
                      })),
                    ]}
                  />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={5}>
                <Form.Item
                  label="Created Date From"
                  name="createdAfter"
                  colon={false}
                  initialValue={null}
                >
                  <DatePicker allowClear className="w-full" />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={5}>
                <Form.Item
                  label="Created Date To"
                  name="createdBefore"
                  colon={false}
                  initialValue={null}
                  dependencies={['createdAfter']}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator(_, value) {
                        const from = getFieldValue('createdAfter');
                        if (!value || !from || !value.isBefore(from, 'day')) return Promise.resolve();
                        return Promise.reject(new Error('Must not be before "Created Date From"'));
                      },
                    }),
                  ]}
                >
                  <DatePicker allowClear className="w-full" />
                </Form.Item>
              </Col>
              <Col className="gutter-row" span={10}>
                <div className="flex">
                  <Button
                    className="w-28"
                    type="primary"
                    shape="round"
                    htmlType="submit"
                    icon={<SearchOutlined />}
                  >
                    Search
                  </Button>
                  <Button
                    className="w-28 ml-2"
                    type="primary"
                    ghost
                    shape="round"
                    htmlType="reset"
                    icon={<SyncOutlined />}
                  >
                    Clear data
                  </Button>
                </div>
              </Col>
            </Row>
          </Form>
        </div>
      </div>

      <div className="content">
        <Table
          className="inbox_table"
          locale={{ emptyText: <IconNotFound /> }}
          rowKey={(user) => user.id}
          loading={loading}
          columns={columns}
          dataSource={users}
          pagination={{
            size: "small",
            current: search.pageValue.page,
            pageSize: search.pageValue.size,
            defaultPageSize: 10,
            total,
            showQuickJumper: true,
            showSizeChanger: true,
            showTotal: (total, range) => ` ${range[0]}-${range[1]} of ${total} `,
          }}
          onChange={(pagination, _, sorter) => setSearch(search => ({
            pageValue: {
              page: pagination.current ?? 1,
              size: pagination.pageSize ?? 10,
              sort: sorter.columnKey,
              order: sorter.order === 'ascend' ? 'asc' : 'desc',
            },
            searchValue: search.searchValue,
          }))}
        />
      </div>

      <ActionModal
        visible={modalType !== 'delete' && !!currentUser}
        modalType={modalType}
        editRecord={currentUser}
        onCancel={() => setCurrentUser(null)}
        onOk={() => {
          setCurrentUser(null);
          setUpdate(update + 1);
        }}
      />

      <BulkActiveModal
        visible={bulkVisible}
        onCancel={() => setBulkVisible(false)}
        onOk={() => setUpdate(update => update + 1)}
      />

      <DeleteAction
        openModalActionDelete={modalType === 'delete' && !!currentUser}
        name="Mobile User"
        type="user"
        editRecord={currentUser}
        onCancel={() => setCurrentUser(null)}
        onOk={() => {
          setCurrentUser(null);
          setUpdate(update + 1);
        }}
      />
    </>
  );
};

export default MemberMobileUser;
